/* eslint no-undef: 0 */
import React from 'react'
import { observable, computed, action, transaction, toJS } from 'mobx'
import * as defaultConst from '../constants/default_constants'
import * as linkConst from '../constants/link_constants'
import ArrayAttrsState from './array_attr'
import BatchOpsStateInit from './batch'
import CItemManipStore from './entity_confs/contractitems/manip'
import CItemTableStore from './entity_confs/contractitems/list'
import CItemsStateInit from './contractitems'
import EtlRunnerInit from './etl_runner'
import EntityConfInit from './entity_confs'
import JSZip from 'jszip'
import JSZipUtils from 'jszip-utils'
import ReportStateInit from './reports'
import moment from 'moment'
import path from 'path-browserify'
import slugify from 'slugify'
import { NotificationManager } from 'react-notifications'
import { RouterStore } from 'mobx-router'
import { saveAs } from 'file-saver/FileSaver'
import { timeRestrictionsSettings } from '../constants/settings_constants'

import DateFilter from '../components/page/filter_date'
import RegionFilter from '../components/page/filter_region'
import HisoricDataFilter from '../components/page/filter_historical'

const CItemsState = CItemsStateInit(ArrayAttrsState)
const ReportState = ReportStateInit(CItemsState)
const EtlRunnerState = EtlRunnerInit(ReportState)
const BatchOpsState = BatchOpsStateInit(EtlRunnerState)
const platformNames = defaultConst.PLATFORMS_NAMES

export default class AppState extends BatchOpsState {
  constructor (views) {
    super()
    this.router = new RouterStore()
    this.views = views
    this.router.publish = observable({
      loading: null,
      offline: null,
      trailer: null
    })
    window.onbeforeunload = (ev) => {
      if (this.cv && this.cv.type === 'entity_detail' && this.isEntityChanged && this.loggedUser) {
        ev.returnValue = this.__('Data changed, really skip saving ?')
        return ev.returnValue
      }
    }
    this.entityLastState = {}
    const { manip, list } = EntityConfInit(this)
    this.manipStores = manip
    this.listStores = list
    this.listQParamsBackup = {}
    this.lastSavedSubEntity = {}
    this.lastListedEntity = null
    this.setLatestVersion()

    this.timeRestriction = {
      ...timeRestrictionsSettings,
      getStages (s) {
        // stage one = disable remove on row and custom disables in form
        // stage two = change button for "View" and remove "Submit" button
        const stages = {
          stageOneRestricted: false,
          stageTwoRestricted: false
        }
        if (s > 0) {
          stages.stageOneRestricted = true
        }
        if (s > 1) {
          stages.stageTwoRestricted = true
        }
        return stages
      },

      // time restriction function to check for selected entity
      checkSubentity (store, { record, timeRestriction }) {
        let stage = 0
        if (timeRestriction && this[timeRestriction]) {
          const itemById =
            record.get('id') &&
            store.cv.record.get(timeRestriction)
              ? store.cv.record.get(timeRestriction).find(i => i.id === record.get('id'))
              : store.cv[timeRestriction] ? store.cv[timeRestriction].find(i => i.id === record.get('id')) : null
          if (itemById) {
            Object.keys(this[timeRestriction]).forEach(key => {
              if (record.get(key)) {
                const currStage = this[timeRestriction][key].stage
                if (this[timeRestriction][key].restriction === 'contract_time_restrictions') {
                  const lastContractDate = store.options.has('timeRestrictions') && store.options.get('timeRestrictions') && store.options.get('timeRestrictions')[0]
                    ? store.options.get('timeRestrictions').reverse()[0].date
                    : moment().format(defaultConst.DATE_FORMAT)
                  if (moment(itemById[key]).isSameOrBefore(lastContractDate)) {
                    if (currStage > stage) stage = currStage
                  }
                } else {
                  if ((this[timeRestriction][key].restrictionToday && moment(itemById[key]).isSameOrBefore(this[timeRestriction][key].restrictionToday, 'day')) ||
                    (moment(itemById[key]).isSameOrBefore(this[timeRestriction][key].restriction, 'day'))) {
                    if (currStage > stage) { stage = currStage }
                  }
                }
              }
            })
          }
        }
        return this.getStages(stage)
      },

      checkEntity (store, record, timeRestriction) {
        let stage = 0
        if (timeRestriction && this[timeRestriction]) {
          Object.keys(this[timeRestriction]).forEach(key => {
            if (record.has('id') && record.get('id') && record.get(key)) {
              const currStage = this[timeRestriction][key].stage
              if ((this[timeRestriction][key].restrictionToday && moment(record.get(key)).isSameOrBefore(this[timeRestriction][key].restrictionToday, 'day')) ||
                (moment(record.get(key)).isSameOrBefore(this[timeRestriction][key].restriction, 'day'))) {
                if (currStage > stage) { stage = currStage }
              }
            }
          })
        }
        return this.getStages(stage)
      },

      checkRow (store, row, { timeRestriction }) {
        let stage = 0

        if (timeRestriction && this[timeRestriction]) {
          Object.keys(this[timeRestriction]).forEach(key => {
            if (row[key]) {
              const currStage = this[timeRestriction][key].stage
              if (this[timeRestriction][key].restriction === 'contract_time_restrictions') {
                const lastContractDate = store.options.has('timeRestrictions') && store.options.get('timeRestrictions') && store.options.get('timeRestrictions')[0]
                  ? store.options.get('timeRestrictions').reverse()[0].date
                  : moment().format(defaultConst.DATE_FORMAT)
                if (moment(row[key]) <= moment(lastContractDate)) {
                  if (currStage > stage) { stage = currStage }
                }
              } else {
                if ((row[key] instanceof String && row[key].indexOf('|') &&
                  row[key].split('|').find(v => moment(v).isSameOrBefore(this[timeRestriction][key].restriction, 'day'))) ||
                  (this[timeRestriction][key].restrictionToday && moment(row[key]).isSameOrBefore(this[timeRestriction][key].restrictionToday, 'day')) ||
                  (moment(row[key]).isSameOrBefore(this[timeRestriction][key].restriction, 'day'))) {
                  if (currStage > stage) stage = currStage
                }
              }
            }
          })
        }
        return stage || false
      }
    }

    const checkVersion = () => {
      this.requester.callExternalRes({
        url: defaultConst.WEBSITE_HOST + defaultConst.WEBSITE_PORT +
        '/version.txt?ts' + Math.round(new Date() / 1000),
        method: 'get'
      })
        .then((res) => {
          this.newVersion = res.data && res.data.trim() !== localStorage.getItem('latest_version')
            ? res.data.trim()
            : null
        })
    }
    checkVersion()
    setInterval(checkVersion, 1000 * 60 * defaultConst.LATEST_VERSION_TEST_INTERVAL)
  }

  @action syncFilmsRating () {
    const cpl = defaultConst.CORS_PROXY_SERVERS
    const doCORSRequest = (idx, imdb_id, printResult) => {
      if (!cpl || cpl.length <= 0) {
        this.cv.itemsStatus.set(this.cv.items[idx].id, defaultConst.STATUS_ERROR)
        return false
      }
      const currentProxy = cpl[idx - cpl.length * (Math.floor(idx / cpl.length))]
      var x = new XMLHttpRequest()
      x.open('GET', `${currentProxy}https://www.imdb.com/title/${imdb_id}/parentalguide`)
      x.onload = x.onerror = () => {
        if (!x.responseText) { // (Math.random() * (100 - 0) + 0) >= 90  (90% change that proxy wont work)
          cpl.splice(cpl.indexOf(currentProxy), 1) // if error, remove proxy URL from list
          if (cpl.length > 0) {
            doCORSRequest(idx, imdb_id, printResult) // repeat action on new list of working proxies
          } else {
            this.cv.itemsStatus.set(this.cv.items[idx].id, defaultConst.STATUS_ERROR)
          }
        } else {
          printResult(x.responseText || '') // otherwise return result
        }
      }
      x.send('')
    }

    const ratingsystem = this.options.get('rating')

    this.cv.items.forEach((item, idx) => {
      let fetched = false
      if (item.imdb_id) {
        this.cv.itemsStatus.set(item.id, defaultConst.STATUS_UPDATING)
        doCORSRequest(idx, item.imdb_id, res => {
          // GET DATA FROM imdb page and parse them as HTML entity
          const doc = new DOMParser().parseFromString(res, 'text/html')
          // Get each rate from list and his anchor tag where data is stored as links
          const ratinglist = doc.querySelectorAll('#certifications-list .ipl-inline-list__item a')
          if (ratinglist) {
            ratinglist.forEach(e => {
              // parse link text as URL to get query string and "certificates" parametter where is <TERRITORY:RATE_SYSTEM>
              const qp = new URL(`https://www.imdb.com${e.getAttribute('href')}`).searchParams.get('certificates')
              const currregion = qp.split(':')[0]
              const currRatingName = qp.split(':')[1]
              if (currregion) {
                const rated = ratingsystem.find(r => r.name === currRatingName)
                if (rated) {
                  if (!item.film_rating.find(r => r.region === currregion)) {
                    item.film_rating.push({
                      rating_system_id: rated.id,
                      region: currregion
                    })
                    fetched = true
                  }
                }
              }
            })
          }

          if (fetched) {
            this.cv.record.set('film_rating_sync', [...this.cv.record.get('film_rating_sync'), true]) // cheat button disable
            this.cv.itemsStatus.set(item.id, defaultConst.STATUS_UPDATED)
          } else {
            this.cv.itemsStatus.set(item.id, defaultConst.STATUS_EVERYTHING_UP_TO_DATE)
          }
        })
      } else {
        this.cv.itemsStatus.set(item.id, defaultConst.STATUS_WARNING_IMBD)
      }
    })
  }

  @action saveLastSavedSubentity (state) {
    const noSave = ['id', 'updated', 'control_timestamp']
    const record = Object.fromEntries(state.record)
    //
    noSave.forEach(n => (record.hasOwnProperty(n)) && delete record[n])
    //
    this.lastSavedSubEntity[state.lastSave] = record
  }

  @action loadLastSavedSubentity (state) {
    let saved = this.lastSavedSubEntity[state.lastSave]

    // load info about pricing into "temporary" variable
    if (saved && saved.flyprivate_platform_tier_pricing_id) {
      const found = this.options.get('flyprivate_pricings').find(i => i.id === saved.flyprivate_platform_tier_pricing_id)
      if (found) {
        this.cv.temporary.set('pricing_select_platform', found.platform)
        this.cv.temporary.set('pricing_select_type', found.type_sale)
        this.cv.temporary.set('pricing_select_tier', found.tier)
      }
    }

    if (saved && saved.file_type) {
      saved = Object.assign({}, {
        file_type: saved.file_type,
        lang: saved.lang,
        regions: saved.regions,
        valid_from: saved.valid_from,
        valid_to: saved.valid_to
      })
      if (saved.parent_id) {
        saved = Object.assign(saved, {
          parent_file_type: saved.parent_file_type,
          parent_id: saved.parent_id
        })
      }
    }

    Object.keys(saved).forEach(k => state.record.set(k, saved[k]))
    state.record.set('control_timestamp', Math.round(new Date().getTime()))
    state.record.set('updated', Math.round(new Date().getTime()))
    if (state.record.get('valid_from') &&
      moment(state.record.get('valid_from')) < moment()) {
      state.record.set('valid_from', moment().format(defaultConst.DATE_FORMAT))
    }
    this._runSubentityValidators(state)
    this._runSubentityGlobalValidator(state, this.cv.record.get(state.lastSave))
    //
    this.addMessage('data from the last save were retrieved', 'info')
  }

  downloadFinancialReports ({ selectionState, customItems }) {
    const items = customItems || this.cv.items
    const selection = selectionState && [...selectionState.get()].map(s => s = items[s] && items[s].id) ||
      this.cv.selection.map(s => s = items[s] && items[s].id)
    const clients = this.options.get('Clients') || this.options.get('clients')
    const getUrl = id =>
      `${Conf.url}/financial_reports/report/${encodeURIComponent(items.find(i => i.id == id).s3path)}?token=${this.authToken}`
    const getName = iid => {
      /*
      let row
      if (this.cv.entityname === 'clients'){
        row = clients.find(c => c.id == this.router.params.id)
      } else {
        row = clients.find(c => c.id == items.find(i => i.id == iid).client_id)
      }
      return (row.name).replace(/\s+/g, '_').toLowerCase() + '_' + Date.now().toString().slice(-4)
      */
      return items.find(i => i.id == iid) && items.find(i => i.id == iid).s3path
        ? items.find(i => i.id == iid).s3path.replace('/', '_')
        : null
    }

    if (selection.length > 0) {
      if (selection.length > 1) {
        const zip = new JSZip()
        let count = 0
        selection.forEach(id => {
          JSZipUtils.getBinaryContent(getUrl(id), (err, data) => {
            if (err) {
              console.log(err, id)
              this.addMessage('Report does not exist - ID ' + id.toString(), 'error')
              return
            }
            zip.file(`${getName(id)}.pdf`, data, { binary: true })
            if (++count == selection.length) {
              zip.generateAsync({ type: 'blob' }).then((content) => saveAs(content, `financial_report_${Date.now()}.zip`))
            }
          })
        })
      } else {
        const idS = parseInt(selection[0])
        JSZipUtils.getBinaryContent(getUrl(idS), (err, data) => {
          if (err) {
            console.log(err, idS)
            this.addMessage('Report does not exist - ID ' + idS, 'error')
            return
          }
          saveAs(new Blob([data], { type: 'application/octet-stream' }), `${getName(idS)}.pdf`)
        })
      }
    }
  }
  @observable lastUsedSelection = []

  @action
  setEntityLastState (entity, data) {
    this.entityLastState[entity] = data
  }

  @observable onClickDisableButton = false
  @action
  onClickDisable (value = false) {
    this.onClickDisableButton = value
    return true
  }

  @observable newVersion = null
  @action
  setLatestVersion () {
    if (this.newVersion) {
      this.dirtyAllUpdate(true)
      localStorage.setItem('latest_version', this.newVersion)
    } else if (!localStorage.getItem('latest_version')) {
      localStorage.setItem('latest_version', defaultConst.LATEST_VERSION.join('\xa0'))
    }
    this.newVersion = null
  }

  @observable changelog = null
  @computed get getChangelog () {
    if (!this.changelog) {
      fetch(path.join(__dirname, '../../RELEASE.md')).then(res => res.text())
        .then(markdown => {
          this.changelog = markdown
        }).catch(e => console.log(e))
    }
    return this.changelog
  }

  @computed get active_platforms () {
    return this.cv.record.has('platforms') ? this.cv.record.get('platforms').split('|') : []
  }

  isPlatformActive (platform) {
    const active = this.cv.record.has('platforms') ? this.cv.record.get('platforms').split('|') : []
    return active.indexOf(platform) >= 0
  }

  platformTitle (platform) {
    const value = platformNames[platform]
    const found = this.options.get('FilmCargoPlatforms') && this.options.get('FilmCargoPlatforms').find((i) => i.value === value)
    return found ? found.text : value
  }

  goTo (view, params, queryParams = {}) {
    this.router.goTo(this.views[view], params, this, queryParams)
  }

  @computed set active_platforms (newval) {
    store.cv.record.set('platforms', newval.join('|'))
  }

  @computed get inactive_platforms () {
    const filmCargoPlatforms = this.options.get('FilmCargoPlatforms')
    const active = this.cv.record.has('platforms') ? this.cv.record.get('platforms').split('|') : []
    const inactive = filmCargoPlatforms.filter((i) => {
      const code = platformNames[i.value]
      return active.indexOf(code) < 0
    })
    return inactive
  }

  @observable runnerNavStatus = null
  @action
  runnerNavStatusSet (value) {
    this.runnerNavStatus = value
  }

  addPlatform (platformOption) {
    const curr = this.cv.record.has('platforms') && this.cv.record.get('platforms') ? this.cv.record.get('platforms').split('|') : []
    if (curr && curr[0] && curr[0] === defaultConst.PLATFORMS_DEFAULT_VALUE) {
      curr.shift()
    }
    const key = platformNames[platformOption.value]
    if (key) {
      curr.push(key)
      this.cv.record.set('platforms', curr.join('|'))
    }
  }

  on404 (route) {
    this.loggedUser && this.addMessage('this route not exists. Redirecting to login ...', 'warning')
    this.router.goTo(this.views.login, {}, this)
  }

  doSave (entityName, data, id) {
    return this.requester.saveEntry(entityName, data, id)
      .catch(this.onRequesterError.bind(this))
  }

  showContractItemDetail (params) {
    this.cv = new CItemManipStore(this)
    return this.cv.load(params.id === '_new' ? null : params.id)
  }

  showEntityUpdateView (entityname, id) {
    id = (id && id !== '_new') ? id : null
    try {
      switch (entityname) {
      default:
        const StoreClass = this.manipStores[entityname]
        if (StoreClass === undefined) {
          return this.on404('unknown entity ' + entityname)
        }
        this.cv = new StoreClass(entityname, id, this.doSave.bind(this))
        this.cv.init()

        if (this.router.recordBackup) { // restore record backup
          this.cv.onLoaded(this.router.recordBackup)
          delete this.router.recordBackup
        } else {
          id && this.requester.getEntry(entityname, id)
            .then(this.cv.onLoaded.bind(this.cv))
            .catch(() => {
              this.skipWarning = true
              this.isEntityChanged = false
              this.cv.flagEditDone()
              this.loggedUser && this.addMessage('Not found. An item with this ID has been changed to another or no longer exists.', 'warning')
              this.router.goTo(this.views.entity_list, { entityname: this.router.params.entityname }, this, { _page: 1 })
              this.onRequesterError.bind(this)
            })
        }
      }
    } catch (e) {
      console.error(e)
    }
  }

  showHistoryDetail ({ entityname, id } = params) {
    this.cv = new this.manipStores.archive(this)
    this.cv.load(entityname, id)
  }

  showEntityListView () {
    const entityname = this.router.params.entityname
    const StoreClass = this.listStores[entityname]
    if (StoreClass === undefined) {
      return this.on404('unknown entity ' + entityname)
    }
    this.cv = new StoreClass(this, entityname, this.requester, this.router, (newQPars) => {
      this.router.goTo(this.router.currentView, this.router.params, this, newQPars)
    })
    if (this.lastEdited !== undefined) {
      // set scrollTo item
      this.cv.scrollTo = linkConst.AUTOSCROLL_ENTITY.includes(entityname) && this.lastEdited
      delete this.lastEdited
    }
    this.cv.init().catch(this.onRequesterError.bind(this))
  }

  onListParamsChange (origParams, origQueryParams) {
    if (this.router.params.entityname !== origParams.entityname) {
      return this.showEntityListView()
    }
    return this.cv.init().catch(this.onRequesterError.bind(this))
  }

  beforeListViewEnter () {
  }

  beforeListViewExit () {
    this.lastListedEntity = this.router.params.entityname

    if (this.router.queryParams && !this.router.queryParams._page) {
      this.router.queryParams._page = 1
    }
    if (this.router.queryParams && !this.router.queryParams._perPage) {
      this.router.queryParams._perPage = this.getUserPreferences('cargo_perPage')
    }

    const queryParamsBackup = Object.assign({}, this.router.queryParams)
    this.listQParamsBackup[this.lastListedEntity] = queryParamsBackup
  }

  beforeEditViewExit (force = false) {
    let leaving = true
    if (!this.skipWarning && this.cv.isEntityChanged && this.loggedUser) {
      leaving = confirm(this.__('Data changed, really skip saving?'))
    }
    !this.skipWarning && leaving && this.cv.flagEditDone()
    this.cv.cancelAutorun && this.cv.cancelAutorun()
    this.lastEdited = Number(this.router.params.id)
    this.skipWarning = false
    return leaving
  }

  addClicked (newFromLast, newFromLastEntity) {
    if (newFromLast === true && newFromLastEntity && this.lastSaved && this.lastSaved[newFromLastEntity]) {
      this.lastSaved[newFromLastEntity].loadLast = true
    }
    this.router.goTo(this.views.entity_detail, {
      entityname: this.router.params.entityname,
      id: '_new'
    }, this)
  }

  onAddClickedCats (store, record, items) {
    // load data
    delete store.lastSaved.FPVFilmCategory.id
    delete store.lastSaved.FPVFilmCategory.control_timestamp
    delete store.lastSaved.FPVFilmCategory.updated
    if (store.lastSaved.FPVFilmCategory.valid_from &&
      moment(store.lastSaved.FPVFilmCategory.valid_from) < moment()) {
      store.lastSaved.FPVFilmCategory.valid_from = moment().format(defaultConst.DATE_FORMAT)
    }
    Object.keys(store.lastSaved.FPVFilmCategory).map(key => {
      record.set(key, store.lastSaved.FPVFilmCategory[key])
    })
    record.set('control_timestamp', Math.round(new Date().getTime()))
    // record.set('updated', new Date())
    // message
    store.addMessage('Data from the last save were retrieved', 'info')
    // validations
    const flyprivateCats = store.cv.subentities.flyprivateCategories
    items = items || [flyprivateCats]
    if (items) {
      this._runSubentityValidators(flyprivateCats)
      this._runSubentityGlobalValidator(flyprivateCats, items)
    }
  }

  showContractItems (params) {
    this.cv = new CItemTableStore(this)
    if (this.lastEdited !== undefined) {
      // set scrollTo
      this.cv.scrollTo = this.lastEdited
      delete this.lastEdited
    }
    this.cv.init().catch(this.onRequesterError.bind(this))
  }

  contractItemsParChange (origParams, origQueryParams) {
    this.cv.init().catch(this.onRequesterError.bind(this))
  }

  detailClicked (row) {
    this.router.goTo(this.views.entity_detail, {
      entityname: this.router.params.entityname,
      id: row[this.cv.pkName || 'id']
    }, this)
  }

  onReturn2list () {
    this.router.goTo(this.views.entity_list, {
      entityname: this.lastListedEntity || this.router.params.entityname
    }, this, this.listQParamsBackup[this.lastListedEntity] || { _page: 1 })
  }

  // messages ///////////////////////////////////////
  @action addMessage = (text, type = 'info', timeout) => {
    timeout = timeout || { success: 2000, info: 4000, warning: 6000, error: 8000 }[type]
    NotificationManager[type](text, type, timeout)
  }

  onRequesterError (err) {
    let msg = null
    if (err.response) {
      const r = err.response
      if (r.status === 401) {
        return
      } else if (r.status >= 500) {
        msg = this.__('backend error, please try again later')
      } else if (r && r.data && r.data.name && r.data.name === 'SequelizeDatabaseError') {
        msg = r.data.original && r.data.original.sqlMessage
          ? r.data.name.concat(' - ') + r.data.original.sqlMessage
          : r.data.name
      } else {
        if (r && r.data.name === 'SequelizeUniqueConstraintError') {
          const errFields = Object.values(r.data.fields).map(v => v.split('-').splice(-1)[0])
          msg = this.__('item already exists, duplicate in') +
            ` '${errFields.join()}' or ID ` + (errFields.length > 1 ? this.__('fields') : this.__('field'))
        } else if (err.message) {
          msg = err.message
        } else {
          msg = r.data.errors[0].message
        }
      }
    } else if (err.config) {
      msg = err.message ? err.message : JSON.stringify(err)
    }
    if (msg) {
      this.addMessage(msg, 'error')
      console.error('onRequesterError:', msg)
    }
    if (this && this.cv) {
      this.cv.loading = false
    }
    throw err
  }

  toSlug (text, lower = true) {
    return slugify(text.toString().toLowerCase()
      .replace(defaultConst.SLUG_REPLACE.from, defaultConst.SLUG_REPLACE.to)
      .replace(/[%&<>|_/:@#?!;,. ]+/g, '-'), {
      replacement: '-',
      lower,
      strict: true
    }).replace(/^-/g, '')
  }

  removeContinents () {
    return v => v && v.value && v.value.indexOf(',') > 0
  }

  getRegionsFromCountries (regions, countries = []) {
    if (!regions || !regions.length) {
      return ['']
    } else if (regions === 'WW') {
      return ['WW']
    }
    const splitRegions = (regions.charAt(0) === '-' ? regions.substring(1) : regions).split(',')
    return regions.charAt(0) === '-'
      ? countries.map(country => !splitRegions
        .find(region => region === country.value) && country.value)
        .filter(result => result && result.length <= 2)
      : splitRegions
  }

  getCatPlatform = (store, state = null, catKey = 'enum_value') => {
    const category = state
      ? state.record.has(catKey) && state.record.get(catKey)
      : store.cv.record.has(catKey) && store.cv.record.get(catKey)
    const platformFilter = state ? state.platformFilter : store.cv.platformFilter
    const catPlatform = category && category.toString().substring(0, category.toString().indexOf('-')).trim().toLowerCase()

    return platformFilter || catPlatform || localStorage.getItem('platformFilter') ||
      (store.options.has('FlyPrivatePlatforms') && store.options.get('FlyPrivatePlatforms')[0].value)
  }

  getCatPlatformFilter (platform) {
    return platform && (v => v.value.toLowerCase().indexOf(platform.toLowerCase()))
  }

  @observable platformFSelector = {}
  @action platformFSelectorUpdate (key, value) {
    this.platformFSelector[key] = value
  }

  @action
  updateStorageField (store, state = null, fieldName = 'enum_value') {
    return (attr, value) => {
      if (state) {
        state[attr] = value
        state.record.delete(fieldName)
        typeof store._runSubentityValidators === 'function' && store._runSubentityValidators(state)
      } else {
        store.cv[attr] = value
        store.cv.record.delete(fieldName)
        typeof store.cv._validateField === 'function' && store.cv._validateField(fieldName, null)
      }
      // save 'platformFilter' to the localStorage
      localStorage.setItem(attr, value)
    }
  }

  @action updateData (state, fieldName, value) {
    value = ((value !== undefined && value !== null) && value.toString().length > 0) ? value : null
    super.updateData(state, fieldName, value)
  }

  @computed get activeLanguages () {
    return this.cv.record.has('attrs')
      ? [defaultConst.DEFAULT_LANG,
        ...(this.cv.record.get('attrs').map(i => i.lang && i.lang.trim().toLowerCase())
          .filter((value, index, self) =>
            self.indexOf(value) === index && value && value.length && value !== defaultConst.DEFAULT_LANG).sort())
      ]
      : [defaultConst.DEFAULT_LANG]
  }

  @observable languageFilter = localStorage.getItem('languageFilter') || defaultConst.DEFAULT_LANG

  @action setActiveLanguage (lang) {
    this.languageFilter = lang && this.activeLanguages.includes(lang) ? lang : defaultConst.DEFAULT_LANG
    localStorage.setItem('languageFilter', this.languageFilter)
  }

  @action updateAttrsLanguage (store, language, remove = false) {
    if (remove) {
      store.cv.record.set('attrs', store.cv.record.get('attrs').filter(
        i => i.lang !== language || i.lang === defaultConst.DEFAULT_LANG)
      )
      this.setActiveLanguage(defaultConst.DEFAULT_LANG)
    } else {
      store.cv.record.get('attrs').push(
        { platform: 'common', name: 'newlang', lang: language, value: '' }
      )
      this.setActiveLanguage(language)
    }
  }

  @action callURL (method, url) {
    return this.requester.call(url, method)
  }

  @observable modalEntity = false
  @action modalEntityControl (value) {
    this.modalEntity = value
    if (this.cv) {
      this.cv.modalIsOpen = value ? true : null
    }
  }

  @observable manipWarning = false
  @observable working = 0
  @action workingUpdate (working) {
    this.working = (working === false ? 0 : (working === true ? 1 : working))
  }

  @observable assetTabs = {
    basic: 1,
    common: 1,
    flyprivate: 1
  }

  @action assetTabEvent (tab, value) {
    this.assetTabs[tab] = value
  }

  @observable queue = []
  @action queuePush (upload) {
    this.queue.push(upload)
  }

  @computed get activeUploads () {
    const activeUploads = this.queue && this.queue.length > 0 && this.queue.filter(
      i => !this.isFileMissing(i) &&
        [defaultConst.STATES_UPLOAD.UPLOADING, defaultConst.STATES_UPLOAD.WAITING].includes(i.state)
    )
    return activeUploads && activeUploads.length > 0
      ? activeUploads.length
      : 0
  }

  // --- date filter ---
  dateFilter = <DateFilter store={this} />
  dateFilterAttr = []

  @observable dateFilterValue = (localStorage && localStorage.getItem('dateFilterValue')) || ''
  @action dateFilterUpdate (date = '') {
    const dateM = date ? moment(date) : ''
    const filterVal = dateM && dateM.isValid() ? dateM.format(defaultConst.DATE_FORMAT) : dateM
    const filterItems = this.router.params.entityname === linkConst.LINK_ARCHIVE
      ? ['archive_date']
      : ((this.cv && this.cv.attrs) || (this.dateFilterAttr && this.dateFilterAttr))
        .filter(i => linkConst.FILTER_DATE.paramItems.includes(i))
    const filterKey = filterItems && filterItems.length ? filterItems.join(',') : ''
    // set date as the new filter... for entity only
    if (typeof this.cv.applyFilters !== 'undefined' && filterVal !== this.dateFilterValue && filterKey) {
      this.router.queryParams[linkConst.FILTER_DATE.paramVal] = filterVal
      this.router.queryParams[linkConst.FILTER_DATE.paramKey] = filterKey
      this.cv.applyFilters()
      this.cv.updateQPars(Object.assign(this.entityLastState[this.cv.entityname], {
        [linkConst.FILTER_DATE.paramVal]: filterVal,
        [linkConst.FILTER_DATE.paramKey]: filterKey
      }))
    }
    // save the global date
    this.dateFilterValue = filterVal
    this.setUserPreferences('dateFilterValue', this.dateFilterValue)
  }

  dateFilterEnable (attrs = null) {
    // check route first
    if (this.router && this.router.currentView && this.router.currentView.rootPath &&
       (this.router.currentView.rootPath === '/batch')
    ) {
      return false
    }
    // then enable filter
    const filterItems = this.router.params.entityname === linkConst.LINK_ARCHIVE
      ? ['archive_date']
      : (attrs && attrs.filter(i => linkConst.FILTER_DATE.paramItems.includes(i)))

    if (filterItems && filterItems.length && this.router.queryParams && this.dateFilterValue &&
      (!this.router.queryParams[linkConst.FILTER_DATE.paramVal] ||
        this.router.queryParams[linkConst.FILTER_DATE.paramVal] !== this.dateFilterValue)
    ) {
      this.router.queryParams[linkConst.FILTER_DATE.paramVal] = this.dateFilterValue
      this.router.queryParams[linkConst.FILTER_DATE.paramKey] = filterItems.join(',')
      this.dateFilterAttr = attrs
    } else if (this.router.queryParams && !this.dateFilterValue) {
      delete this.router.queryParams[linkConst.FILTER_DATE.paramVal]
      delete this.router.queryParams[linkConst.FILTER_DATE.paramKey]
    }
    return (filterItems && filterItems.length) || false
  }

  // --- region filter ---
  regionFilterOpened = false
  regionFilter = <RegionFilter store={this} />
  regionFilterAttr = []

  @observable regionFilterValue = (localStorage && localStorage.getItem('regionFilterValue')) || ''
  @observable regionFilterModal = false
  @action regionFilterModalOpen (value = true) {
    if (value === false) {
      this.regionFilterOpened = false
    }
    this.regionFilterModal = value
    if (this.cv) {
      this.cv.modalIsOpen = value
    }
  }

  regionFilterHistory = localStorage && localStorage.getItem('regionFilterHistory') && localStorage.getItem('regionFilterHistory').length
    ? localStorage.getItem('regionFilterHistory').split(',')
    : []

  @action regionFilterUpdate (region = '') {
    const filterItems = ((this.cv && this.cv.attrs) || (this.regionFilterAttr && this.regionFilterAttr))
      .filter(i => linkConst.FILTER_REGION.paramItems.includes(i))
    const filterKey = filterItems && filterItems.length ? filterItems[0] : null
    // add region to the history
    if (region && region !== 'WW' && !this.regionFilterHistory.includes(region)) {
      this.regionFilterHistory.push(region)
      this.regionFilterHistory.length > linkConst.FILTER_REGION.paramHistory && this.regionFilterHistory.shift()
    }
    localStorage.setItem('regionFilterHistory', this.regionFilterHistory.join())
    // set region as the new filter - for entity only
    if (typeof this.cv.applyFilters !== 'undefined' && region !== this.regionFilterValue && filterKey) {
      this.router.queryParams[linkConst.FILTER_REGION.paramVal] = region
      this.router.queryParams[linkConst.FILTER_REGION.paramKey] = filterKey
      this.cv.applyFilters()
      this.cv.updateQPars(Object.assign(this.entityLastState[this.cv.entityname], {
        [linkConst.FILTER_REGION.paramVal]: region,
        [linkConst.FILTER_REGION.paramKey]: filterKey
      }))
    }
    // set region and close modal window
    this.regionFilterValue = region
    this.setUserPreferences('regionFilterValue', this.regionFilterValue)
    this.regionFilterModalOpen(false)
  }

  regionFilterEnable (attrs = null) {
    // check route first
    if (this.router && this.router.currentView && this.router.currentView.rootPath &&
       (this.router.currentView.rootPath === '/batch')
    ) {
      return false
    }
    // then enable filter
    const filterItems = attrs && attrs.filter(i => linkConst.FILTER_REGION.paramItems.includes(i))
    if (filterItems && filterItems.length && this.router.queryParams && this.regionFilterValue &&
       (!this.router.queryParams[linkConst.FILTER_REGION.paramVal] ||
         this.router.queryParams[linkConst.FILTER_REGION.paramVal] !== this.regionFilterValue)) {
      this.router.queryParams[linkConst.FILTER_REGION.paramVal] = this.regionFilterValue
      this.router.queryParams[linkConst.FILTER_REGION.paramKey] = filterItems[0]
      //
      this.regionFilterAttr = attrs
    } else if (this.router.queryParams && !this.regionFilterValue) {
      delete this.router.queryParams[linkConst.FILTER_REGION.paramVal]
      delete this.router.queryParams[linkConst.FILTER_REGION.paramKey]
    }
    return (filterItems && filterItems.length) || false
  }

  /* FILTER FOR DISABLING PAST DATA DATA */
  @observable historicDataFilterActive = (localStorage && localStorage.getItem('historyFilterData')) || false
  historicDataFilter = <HisoricDataFilter store={this} />
  historicDataFilterAttr = []

  historicDataFilterEnable (attrs = null) {
    if (this.router && this.router.currentView && this.router.currentView.rootPath && this.router.currentView.rootPath === '/batch') {
      return false
    }

    if (linkConst.HISTORY_FILTER.entityRestriction.includes(this.cv.entityname)) {
      return false
    }

    const filterItems = attrs && attrs.filter(i => linkConst.HISTORY_FILTER.paramItems.includes(i))

    if (filterItems && filterItems.length && this.router.queryParams && this.historicDataFilterActive === true &&
       (this.router.queryParams[linkConst.HISTORY_FILTER.paramVal] === undefined ||
         this.router.queryParams[linkConst.HISTORY_FILTER.paramVal] === false)) {
      this.router.queryParams[linkConst.HISTORY_FILTER.paramVal] = this.historicDataFilterActive
      this.router.queryParams[linkConst.HISTORY_FILTER.paramKey] = filterItems[0]
      //
    } else if (this.router.queryParams && !this.historicDataFilterActive) {
      delete this.router.queryParams[linkConst.HISTORY_FILTER.paramVal]
      delete this.router.queryParams[linkConst.HISTORY_FILTER.paramKey]
    }

    return (filterItems && filterItems.length) || false
  }

  @action historicDataFilterUpdate = () => {
    const filterItems = ((this.cv && this.cv.attrs) || (this.historicDataFilterAttr && this.historicDataFilterAttr))
      .filter(i => linkConst.HISTORY_FILTER.paramItems.includes(i))
    const filterKey = filterItems && filterItems.length ? filterItems[0] : null

    if (this.historicDataFilterActive) {
      this.historicDataFilterActive = false
      this.setUserPreferences('historyFilterData')
    } else {
      this.historicDataFilterActive = true
      this.setUserPreferences('historyFilterData', true)
    }

    if (typeof this.cv.applyFilters !== 'undefined' && filterKey) {
      this.router.queryParams[linkConst.HISTORY_FILTER.paramVal] = this.historicDataFilterActive
      this.router.queryParams[linkConst.HISTORY_FILTER.paramKey] = filterKey
      this.cv.applyFilters()
      this.cv.updateQPars(Object.assign(this.entityLastState[this.cv.entityname], {
        [linkConst.HISTORY_FILTER.paramVal]: this.historicDataFilterActive,
        [linkConst.HISTORY_FILTER.paramKey]: filterKey
      }))
    }
  }

  @observable additionalDataFilterActive = false

  // helper for filmcargo help
  helpFor = path => {
    const filmcargoHelp = this.options.has('filmcargo_help') && this.options.get('filmcargo_help')
    const filmcargoHelpRecord = filmcargoHelp && this.options.get('filmcargo_help').find(h => h.path === path)
    return filmcargoHelpRecord
      ? filmcargoHelpRecord.value
      : null
  }

  // flyprivate_copy_modal
  @observable fp_pricings_copy_modal = false
  @action toggleFPPricingsCopyModal = () => {
    this.fp_pricings_copy_modal = !this.fp_pricings_copy_modal
  }

  // flyprivate_copy_modal
  @observable fp_cats_copy_modal = false
  @action toggleFPCatsCopyModal = () => {
    this.fp_cats_copy_modal = !this.fp_cats_copy_modal
  }

  // contract rights modal
  @observable rightsCopyModal = false
  @action toggleRightsCopyModal = () => {
    this.rightsCopyModal = !this.rightsCopyModal
  }

  // set as inactive
  @observable setAsInactive = false
  @action toggleSetAsInactive = () => {
    this.setAsInactive = !this.setAsInactive
  }

  // save custom filter options
  @observable customFilterOptions = {}
  @action saveCustomFilterOptions = (e, f) => {
    this.customFilterOptions[e] = f
  }

  // save custom filter parameters
  @observable customFilterParameters = {}
  @action saveCustomFilterParameters = (e, f) => {
    this.customFilterParameters[e] = f
  }

  // episodes_series_gen_modal
  @observable episodes_series_gen_modal = false
  @action toggleEpisodesSeriesGenModal = (data = {}) => {
    this.episodes_series_gen_modal = this.episodes_series_gen_modal ? false : data
  }

  @action generateEpisodesSeries = () => {
    const { mode, parentId, reverseOrder } = this.episodes_series_gen_modal
    const data = this.cv.subentities.seriesEpisodesGen.record
    const parent = this.options.get('films').find(f => parseInt(f.id) === parseInt(parentId))
    const parentTitle = parent ? parent.title : null
    const seasons = this.options.get('seasons')
    const from = parseInt(data.get('from'))
    const to = parseInt(data.get('to'))
    const titleType = data.get('title_type') || null
    const parentParent = this.options.get('seasons').find(f => parseInt(f.id) === parseInt(parentId))
    const parentParentTitle = parentParent && parentParent.parent_id
      ? this.options.get('films').find(f => parseInt(f.id) === parseInt(parentParent.parent_id)).title.replace(' - series', '')
      : parentTitle ? parentTitle.replace(' - series', '') : null

    const slug = t => t.toLowerCase().replace(/[^\w ]+/g, '').replace(/ +/g, '-') // slug fnc
    const fns = {
      // generate titles from name, season_no and episode_no
      season: (name, s) => `${name} - season ${s}`,
      seasonSlug: (name, s) => `${slug(name)}-season-${s}`,
      // episode name setup
      episode: (name, s, e) => `${name} - S${s} E${e}`,
      episodeSlug: (name, s, e) => `${slug(name)}-s${s}e${e}`,
      // titles
      titleFromSeries: (s, m = s.match(/([-\s\s\w]+) - series$/i)) => s.includes('- series') ? (m ? m[1] : s) : s, // get name from series title
      titleFromSeason: (s, m = s.match(/([-\s\s\w]+) - season(?: [\d]+)?$/i)) => s.includes('- season') ? (m ? m[1] : s) : s // get name from season title
    }

    let numberSaved = (to - from) + 1
    for (let i = from; i <= to; i++) {
      let newItem = null

      const name = mode === 'seasons'
        ? fns.titleFromSeries(parentTitle)
        : fns.titleFromSeason(parentTitle)
      const cTitle = mode === 'seasons'
        ? fns.season(name, i)
        : fns.episode(name, seasons.find(s => parseInt(s.id) === parseInt(parentId)).season_no, i)
      const cSlug = mode === 'seasons'
        ? fns.seasonSlug(name, i)
        : fns.episodeSlug(name, seasons.find(s => parseInt(s.id) === parseInt(parentId)).season_no, i)

      if (mode === 'seasons') {
        if (!seasons.find(s => parseInt(s.parent_id) === parseInt(parentId) && parseInt(s.season_no) === i)) {
          newItem = {
            slug: cSlug,
            title: cTitle,
            video_type: 'SEASON',
            parent_id: parentId,
            season_no: i,
            reverse_order: reverseOrder,
            attrs: [{
              lang: defaultConst.DEFAULT_LANG, name: 'name', platform: 'common', value: (titleType ? cTitle : parentParentTitle)
            }]
          }
        }
      } else {
        if (!this.options.get('episodes')
          .find(e => e.parent_id === seasons.find(s => parseInt(s.id) === parseInt(parentId)).id && parseInt(e.episode_no) === i)) {
          newItem = {
            slug: cSlug,
            title: cTitle,
            video_type: 'EPISODE',
            parent_id: parentId,
            episode_no: i,
            attrs: [{
              lang: defaultConst.DEFAULT_LANG, name: 'name', platform: 'common', value: (titleType ? cTitle : parentParentTitle)
            }]
          }
        }
      }

      if (newItem) {
        this.requester.call('/films', 'post', newItem).then(({ data }) => {
          const type = data.video_type === 'EPISODE' ? 'episodes' : 'seasons'
          this.options.set(type, [...this.options.get(type), data])
          this.options.set('films', [...this.options.get('films'), data])
        }).catch(this.onRequesterError.bind(this))
      } else {
        numberSaved--
      }
    }

    if (numberSaved === (to - from + 1)) {
      this.addMessage(`All ${mode} saved`, 'success')
    } else {
      this.addMessage(`All ${mode} without colision saved. (${numberSaved}/${to - from + 1})`, 'warning')
    }
    this.toggleEpisodesSeriesGenModal()
  }

  @action solvedAsset (assetId, record = null, filmId = null) {
    this.skipWarning = true
    const row = Object.assign(
      {},
      record || this.cv.items.find(i => i.id === assetId),
      { updated_by: this.loggedUser.email, source: 'datacleansing', id: undefined }
    )

    this.requester.call(`/films/${filmId}`, 'get')
      .then(result => {
        result.data.youtube_asset_info.push(row)
        return this.requester.call(`/films/${filmId}`, 'put', result.data)
      })
      .catch(this.onRequesterError.bind(this))
      .then(_ => {
        return this.requester.call(`/youtube_assets/${assetId}`, 'delete')
          .then(_ => {
            if (record) {
              this.onReturn2list()
            } else {
              this.cv.items.remove(this.cv.items.find(i => i.id === assetId))
            }
            this.addMessage('Successfully solved #' + assetId, 'info')
          })
          .catch(this.onRequesterError.bind(this))
      })
      .catch(this.onRequesterError.bind(this))
  }

  @action removedAsset (assetId, record = null) {
    this.requester.call(`/youtube_assets/${assetId}`, 'delete')
      .then(_ => {
        if (record) {
          this.onReturn2list()
        } else {
          this.cv.items.remove(this.cv.items.find(i => i.id === assetId))
        }
        this.addMessage('Successfully removed #' + assetId, 'success')
      })
      .catch(this.onRequesterError.bind(this))
  }
}
